import { tv, type VariantProps } from "tailwind-variants";

import { ContentMimeTypeTag } from "@virtual-brain-lib/frontend-design-system/components";
import { cn, type VariantSlotsToClassNames } from "@virtual-brain-lib/frontend-design-system/utils";
import { DOCUMENT_TYPE_BY_MIME_TYPE, type TaggableMimeType } from "@virtual-brain-lib/types";

import { Icon } from "./Icon";
import { Span, type SpanProps } from "./Span";

export const documentItemVariants = tv({
	slots: {
		base: "flex min-w-0 items-center gap-1.5 rounded-sm px-1 py-0.5 text-left",
		name: "truncate",
	},
	variants: {
		interactive: {
			true: {
				base: "cursor-pointer transition-colors hover:bg-secondary-hover",
			},
			false: {
				base: "",
			},
		},
	},
	defaultVariants: {
		interactive: false,
	},
});

export type DocumentItemVariant = VariantProps<typeof documentItemVariants>;

function isKnownMimeType(value: string | undefined): value is TaggableMimeType {
	return value !== undefined && Object.hasOwn(DOCUMENT_TYPE_BY_MIME_TYPE, value);
}

export interface DocumentItemProps {
	readonly name: string;
	readonly mimeType?: string;
	readonly onClick?: () => void;
	readonly className?: string;
	readonly classNames?: VariantSlotsToClassNames<typeof documentItemVariants>;
	readonly tooltip?: SpanProps["tooltip"];
}

export function DocumentItem({
	name,
	mimeType,
	onClick,
	className,
	classNames,
	tooltip,
}: DocumentItemProps) {
	const { base, name: nameSlot } = documentItemVariants({ interactive: Boolean(onClick) });

	const leading = isKnownMimeType(mimeType) ? (
		<ContentMimeTypeTag mimeType={mimeType} size="xs" />
	) : (
		<Icon color="defaultLight" name="file" size="xs" />
	);

	const content = (
		<>
			{leading}
			<Span
				className={nameSlot({ className: classNames?.name })}
				color="default"
				variant="body-xs"
				tooltip={tooltip}
			>
				{name}
			</Span>
		</>
	);

	if (onClick) {
		return (
			<button
				className={base({ className: cn(className, classNames?.base) })}
				type="button"
				onClick={onClick}
			>
				{content}
			</button>
		);
	}

	return <div className={base({ className: cn(className, classNames?.base) })}>{content}</div>;
}
